import React from 'react';
import { useDispatch } from 'react-redux';
import { PriceInformation } from './components/price-information';
import { StockStatusStates } from 'src/components/stock-status/types';
import { useViewportContext } from '../../../viewport/context';
import Rating from '../rating';

// NOTE: Importing this file needs to be relative for storybook to not crash...
import { productPriceClick } from '../../../../areas/product/actions/product';
import { logRevenueEvent } from 'src/areas/product/components/price-list/price-row/utils';

import * as S from './styles';

import { Store } from '../../../../types';

type Props = {
  id?: Store['id'];
  'data-test'?: String;
  name?: Store['name'];
  hasLogo: Store['hasLogo'];
  logoUrl?: string | null;
  logoWidth: number;
  featured: Store['featured'];
  pathname: Store['pathName'];
  rating: Store['userReviewSummary']['rating'];
  numericRating?: boolean;
  reviewCount: Store['userReviewSummary']['count'];
  renderMode?: string;
  stockStatus?: StockStatusStates | null;
  price?: number | null;
  priceId?: string;
  isDeal?: boolean;
  productId?: number;
};

export const StoreCard = (props: Props) => {
  const { isMobile } = useViewportContext();
  const {
    id,
    'data-test': dataTest,
    name,
    hasLogo,
    logoUrl,
    logoWidth,
    featured,
    pathname,
    rating,
    numericRating = false,
    reviewCount,
    renderMode,
    stockStatus,
    price,
    productId,
    priceId,
    isDeal = false,
  } = props;
  const dispatch = useDispatch();

  const count = reviewCount && reviewCount > 500 ? '500+' : reviewCount;

  const highResolutionLogo = logoUrl
    ? { url: logoUrl, width: logoWidth }
    : null;
  const featuredStore = Boolean(featured);
  const trackClickOut = () => {
    if (!priceId || !productId) {
      return;
    }

    // Track revenue and event on click out
    logRevenueEvent(String(priceId), productId);
    dispatch(
      productPriceClick(productId, price, featuredStore, '', false, 'StoreCard')
    );
  };

  const renderContent = () => (
    <>
      {id && name && (
        <S.Logo
          storeId={id}
          name={name}
          hasImage={Boolean(hasLogo)}
          featured={featuredStore}
          logoUrl={highResolutionLogo?.url}
          width={highResolutionLogo?.width}
          showHighResolutionLogo={Boolean(highResolutionLogo)}
        />
      )}
      {rating && rating !== 0 && (
        <>
          <Rating
            type="store"
            rating={rating || 0}
            numericRating={numericRating}
            disabled={!featuredStore}
          />

          {isMobile && price ? null : <S.Count>({count})</S.Count>}
        </>
      )}
      {price && (
        <PriceInformation
          price={price}
          stockStatus={stockStatus}
          isDeal={isDeal}
          featured={featuredStore}
        />
      )}
    </>
  );

  if (!pathname) {
    return <S.UnfeaturedStore>{renderContent()}</S.UnfeaturedStore>;
  }

  return (
    <S.StoreLink
      data-test={dataTest}
      onClick={trackClickOut}
      to={pathname}
      isExternal={renderMode === 'native'}
      target="_blank"
    >
      {renderContent()}
    </S.StoreLink>
  );
};
